import React, { useState } from "react"
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  LinearProgress,
  FormHelperText,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from '@mui/icons-material/Add'
import { TrashIcon } from "../../icons"
import { fileUpload, fileDelete } from "../../api/uploadApi"
import ValidationErrorText from "../common/ValidationErrorText"

const initialForm = {
  professorName: "",
  designation: "",
  experience: "",
  qualification: "",
  image: "" // only store URL
}

export default function FacultyTeam({ label, value = [], onChange, readOnly = false,fieldType,fieldErrors }) {
  const forms = value

  // Manage per-index uploading state (for UI/progress)
  const [uploadingIdx, setUploadingIdx] = useState(null)
  const [progress, setProgress] = useState(0)
  const [uploadError, setUploadError] = useState("")

  const updateForm = (idx, changes) => {
    const newForms = forms.map((f, i) =>
      i === idx ? { ...f, ...changes } : f
    )
    onChange && onChange(newForms)
  }

  // Upload handler: just set image:url after upload
  const handleImageChange = (idx) => async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Only allow jpg/png, max 2MB
    const isValidFormat = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)
    const isValidSize = file.size <= 2 * 1024 * 1024
    if (!isValidFormat) {
      setUploadError("Invalid file format")
      return
    }
    if (!isValidSize) {
      setUploadError("File too large (max 2MB)")
      return
    }

    setUploadError("")
    setUploadingIdx(idx)
    setProgress(0)

    try {
      const data = await fileUpload(file, (prog) => setProgress(prog))
      updateForm(idx, { image: data.url || "" })
    } catch {
      setUploadError("Image upload failed!")
    } finally {
      setUploadingIdx(null)
      setProgress(0)
    }
  }

  // Async delete handler: delete backend and clear image url
  const handleRemoveImage = (idx) => async (e) => {
    e?.stopPropagation && e.stopPropagation()
    const { image } = forms[idx]
    if (image) {
      try {
        await fileDelete(image)
      } catch {
        setUploadError("Failed to delete image from server")
        return
      }
    }
    updateForm(idx, { image: "" })
  }

  const handleChange = (idx, field) => (e) => {
    updateForm(idx, { [field]: e.target.value })
  }

  const handleAddMore = () => {
    onChange && onChange([...forms, { ...initialForm }])
  }

  const handleRemove = (idx) => {
    onChange && onChange(forms.filter((_, i) => i !== idx))
  }

  const inputStyle = {
    height: 38,
    fontSize: 13,
    '& input': {
      height: '100%',
      padding: '0 14px',
      fontSize: 13,
    },
  }
  const labelStyle = {
    fontSize: 13,
    fontWeight: 500,
    marginBottom: "4px",
  }

  return (
    <Box>
      <Typography fontSize="14px" fontWeight="500" mb={1}>
        {label}
      </Typography>

      {forms.map((form, index) => (
        <Box
          key={index}
          sx={{
            borderRadius: 2,
            mb: 3,
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          {/* Image Upload Box */}
          <Box>
          <Box
            sx={{
              width: { xs: 120, sm: 150, md: 140, lg: 140, xl: 180 },
              height: { xs: 120, sm: 150, md: 140, lg: 140, xl: 180 },
              border: "2px dashed #7b9fe0",
              borderRadius: 2,
              position: "relative",
              cursor: readOnly ? "not-allowed" : "pointer",
              overflow: "hidden",
              flexShrink: 0,
              background: uploadingIdx === index ? "#f4f4f4" : undefined,
            }}
            onClick={() =>
              !readOnly && uploadingIdx !== index && document.getElementById(`faculty-image-upload-${index}`).click()
            }
          >
            {uploadingIdx === index ? (
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress size={28} />
                <Typography fontSize="13px" mt={1}>Uploading...</Typography>
                <Box sx={{ width: "80%", mt: 1 }}>
                  <LinearProgress value={progress} variant="determinate" sx={{ height: 6, borderRadius: 2 }} />
                </Box>
              </Box>
            ) : form.image ? (
              <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
                <img
                  src={form.image}
                  alt="professor"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {/* Remove Icon */}
                {!readOnly && (
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage(index)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255,255,255,0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,1)",
                      },
                    }}
                    disabled={uploadingIdx === index}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#2c3e9e",
                  fontWeight: "bold",
                  px: 1,
                  textAlign: "center",
                  fontSize: 14,
                  lineHeight: 1.3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.5,
                    color: '#363853',
                    fontWeight: '600',
                    fontSize: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <AddIcon sx={{ fontSize: 24, fill: '#1075FF', }} />
                  Upload Professor Profile image
                </Typography>
                <Typography sx={{ mt: 1.5, color: '#363853', fontWeight: '500', fontSize: '10px', }}>
                  Upload a high-quality JPG/PNG, minimum 300×300 pixels,
                  maximum 2MB.
                </Typography>
              </Box>
            )}
            <input
              id={`faculty-image-upload-${index}`}
              type="file"
              accept="image/png, image/jpeg"
              style={{ display: "none" }}
              disabled={readOnly || uploadingIdx === index}
              onChange={handleImageChange(index)}
            />
            {uploadingIdx === index && uploadError && (
              <Typography color="error" fontSize={12} mt={1} sx={{ position: "absolute", bottom: 6, left: 6, right: 6 }}>
                {uploadError}
              </Typography>
            )}

          </Box>
          <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].image`]}/>
          </Box>

          {/* Inputs */}
          <Grid container spacing={2} flex={1}>
            <Grid xs={12} sm={6}>
              <Typography sx={labelStyle} mb={0.5}>
                Professor Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={form.professorName}
                onChange={handleChange(index, "professorName")}
                placeholder="Enter Professor Name"
                InputProps={{ sx: inputStyle, readOnly }}
                disabled={readOnly}
              />
            <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].professorName`]}/>
            </Grid>
            <Grid xs={12} sm={6}>
              <Typography sx={labelStyle} mb={0.5}>
                Designation
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={form.designation}
                onChange={handleChange(index, "designation")}
                placeholder="Enter Designation"
                InputProps={{ sx: inputStyle, readOnly }}
                disabled={readOnly}
              />
            <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].designation`]}/>
            </Grid>
            <Grid xs={12} sm={6}>
              <Typography sx={labelStyle} mb={0.5}>
                Total Years of Experience
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={form.experience}
                onChange={handleChange(index, "experience")}
                placeholder="Enter Years of Experience"
                InputProps={{ sx: inputStyle, readOnly }}
                disabled={readOnly}
              />
            <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].experience`]}/>
            </Grid>
            <Grid xs={12} sm={6}>
              <Typography sx={labelStyle} mb={0.5}>
                Highest Qualification
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={form.qualification}
                onChange={handleChange(index, "qualification")}
                placeholder="Enter Qualification"
                InputProps={{ sx: inputStyle, readOnly }}
                disabled={readOnly}
              />
            <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].qualification`]}/>
            </Grid>

            {/* Remove Button: hidden for first 3 forms */}
            {index >= 3 && !readOnly && (
              <Grid xs={12}>
                <Button
                  variant="contained"
                  sx={{
                    background: '#FF4141',
                    color: '#fff',
                    textTransform: "capitalize",
                    fontWeight: '400',
                  }}
                  startIcon={<TrashIcon style={{ fill: '#fff' }} />}
                  onClick={() => handleRemove(index)}
                >
                  Remove
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      ))}

      {/* Add More Button */}
      {!readOnly && (
        <Button
          sx={{
            color: '#1075FF',
            border: '1px solid #1075FF',
            textTransform: 'capitalize',
            px: 3,
            mt: 1
          }}
          onClick={handleAddMore}
        >
          + Add More
        </Button>
      )}
    </Box>
  )
}
